import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, Save, Calendar, FileText,
  ChevronDown, Search, Check, ArrowRightLeft, Calculator
} from 'lucide-react';
import '../styles/mobile.css';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function MobileBookkeepingCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [showAccountPicker, setShowAccountPicker] = useState(null);
  const [accountSearch, setAccountSearch] = useState('');

  const [entry, setEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    document_ref: '',
    lines: [
      { account_number: '', account_name: '', debit: 0, credit: 0, description: '' },
      { account_number: '', account_name: '', debit: 0, credit: 0, description: '' }
    ]
  });

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!token) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/accounts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        setAccounts(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAccounts();
  }, [token]);

  const selectAccount = (lineIndex, account) => {
    const newLines = [...entry.lines];
    newLines[lineIndex] = {
      ...newLines[lineIndex],
      account_number: account.account_number,
      account_name: account.name
    };
    setEntry(prev => ({ ...prev, lines: newLines }));
    setShowAccountPicker(null);
    setAccountSearch('');
  };

  const updateLine = (index, field, value) => {
    const newLines = [...entry.lines];
    
    // If updating debit, clear credit and vice versa
    if (field === 'debit' && value > 0) {
      newLines[index] = { ...newLines[index], debit: value, credit: 0 };
    } else if (field === 'credit' && value > 0) {
      newLines[index] = { ...newLines[index], credit: value, debit: 0 };
    } else {
      newLines[index] = { ...newLines[index], [field]: value };
    }
    
    setEntry(prev => ({ ...prev, lines: newLines }));
  };

  const addLine = () => {
    setEntry(prev => ({
      ...prev,
      lines: [...prev.lines, { account_number: '', account_name: '', debit: 0, credit: 0, description: '' }]
    }));
  };

  const removeLine = (index) => {
    if (entry.lines.length > 2) {
      setEntry(prev => ({
        ...prev,
        lines: prev.lines.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const totalDebit = entry.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = entry.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
    
    return { totalDebit, totalCredit, isBalanced, diff: totalDebit - totalCredit };
  };

  const handleSave = async () => {
    const totals = calculateTotals();
    
    if (!totals.isBalanced) {
      alert(`Verifikationen balanserar inte! Differens: ${totals.diff.toFixed(2)} kr`);
      return;
    }
    
    if (entry.lines.some(l => !l.account_number)) {
      alert('Alla rader måste ha ett konto');
      return;
    }
    
    if (!entry.description) {
      alert('Ange en beskrivning');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        date: entry.date,
        description: entry.description,
        document_ref: entry.document_ref,
        lines: entry.lines.map(line => ({
          type: 'account',
          account_number: line.account_number,
          account_name: line.account_name,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
          description: line.description
        })),
        total_debit: totals.totalDebit,
        total_credit: totals.totalCredit
      };

      const response = await fetch(`${API_BASE}/api/journal-entries`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        navigate('/m/bookkeeping');
      } else {
        const error = await response.json();
        alert(error.detail || 'Kunde inte spara verifikation');
      }
    } catch (err) {
      console.error('Save error:', err);
      alert('Fel vid sparande');
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals();
  const filteredAccounts = accounts.filter(a => 
    a.account_number?.includes(accountSearch) ||
    a.name?.toLowerCase().includes(accountSearch.toLowerCase())
  );

  // Common accounts for quick access
  const commonAccounts = accounts.filter(a => 
    ['1930', '2440', '3001', '4000', '5010', '6110'].includes(a.account_number)
  );

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m/bookkeeping')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('bookkeeping.newEntry')}</div>
        </div>
        <button 
          className="mobile-header-btn" 
          onClick={handleSave}
          disabled={saving || !totals.isBalanced}
          style={{ background: totals.isBalanced ? 'rgba(255,255,255,0.25)' : 'rgba(239,68,68,0.3)' }}
        >
          <Save size={20} />
        </button>
      </header>

      {/* Content */}
      <main className="mobile-content" style={{ paddingBottom: '160px' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="mobile-spinner" />
          </div>
        ) : (
          <>
            {/* Entry Info */}
            <div className="mobile-card">
              <div className="mobile-input-group">
                <label className="mobile-label">{t('common.date')} *</label>
                <input
                  type="date"
                  className="mobile-input"
                  value={entry.date}
                  onChange={(e) => setEntry(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              
              <div className="mobile-input-group">
                <label className="mobile-label">{t('common.description')} *</label>
                <input
                  type="text"
                  className="mobile-input"
                  placeholder="T.ex. Kontorsmaterial, Inköp, etc."
                  value={entry.description}
                  onChange={(e) => setEntry(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              
              <div className="mobile-input-group" style={{ marginBottom: 0 }}>
                <label className="mobile-label">Verifikationsnummer</label>
                <input
                  type="text"
                  className="mobile-input"
                  placeholder="Valfritt (t.ex. kvittonummer)"
                  value={entry.document_ref}
                  onChange={(e) => setEntry(prev => ({ ...prev, document_ref: e.target.value }))}
                />
              </div>
            </div>

            {/* Balance Status */}
            <div 
              className="mobile-card" 
              style={{ 
                background: totals.isBalanced ? '#dcfce7' : '#fee2e2',
                borderLeft: `4px solid ${totals.isBalanced ? '#16a34a' : '#dc2626'}`
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ArrowRightLeft size={24} color={totals.isBalanced ? '#16a34a' : '#dc2626'} />
                <div>
                  <div style={{ fontWeight: 600, color: totals.isBalanced ? '#16a34a' : '#dc2626' }}>
                    {totals.isBalanced ? 'Balanserat ✓' : 'Ej balanserat'}
                  </div>
                  <div style={{ fontSize: '13px', color: totals.isBalanced ? '#15803d' : '#b91c1c' }}>
                    Debet: {totals.totalDebit.toLocaleString('sv-SE')} kr | Kredit: {totals.totalCredit.toLocaleString('sv-SE')} kr
                    {!totals.isBalanced && ` | Diff: ${totals.diff.toFixed(2)} kr`}
                  </div>
                </div>
              </div>
            </div>

            {/* Entry Lines */}
            <div className="mobile-section-header">
              <h3 className="mobile-section-title">Konteringsrader</h3>
              <button className="mobile-section-action" onClick={addLine}>
                <Plus size={16} /> Lägg till
              </button>
            </div>

            {entry.lines.map((line, idx) => (
              <div key={idx} className="mobile-card" style={{ position: 'relative' }}>
                {entry.lines.length > 2 && (
                  <button
                    onClick={() => removeLine(idx)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      background: '#fee2e2',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} color="#dc2626" />
                  </button>
                )}
                
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: 600, 
                  color: '#64748b', 
                  marginBottom: '12px',
                  textTransform: 'uppercase'
                }}>
                  Rad {idx + 1}
                </div>

                {/* Account Selection */}
                <div className="mobile-input-group">
                  <label className="mobile-label">{t('bookkeeping.account')} *</label>
                  <button
                    onClick={() => setShowAccountPicker(idx)}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      background: line.account_number ? '#f0fdf4' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    <span style={{ color: line.account_number ? '#1e293b' : '#94a3b8' }}>
                      {line.account_number ? `${line.account_number} - ${line.account_name}` : 'Välj konto...'}
                    </span>
                    <ChevronDown size={16} color="#64748b" />
                  </button>
                </div>

                {/* Debit / Credit */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label className="mobile-label" style={{ color: '#16a34a' }}>{t('bookkeeping.debit')}</label>
                    <input
                      type="number"
                      className="mobile-input"
                      value={line.debit || ''}
                      onChange={(e) => updateLine(idx, 'debit', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      style={{ 
                        borderColor: line.debit > 0 ? '#16a34a' : '#e2e8f0',
                        background: line.debit > 0 ? '#f0fdf4' : 'white'
                      }}
                    />
                  </div>
                  <div>
                    <label className="mobile-label" style={{ color: '#dc2626' }}>{t('bookkeeping.credit')}</label>
                    <input
                      type="number"
                      className="mobile-input"
                      value={line.credit || ''}
                      onChange={(e) => updateLine(idx, 'credit', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      min="0"
                      step="0.01"
                      style={{ 
                        borderColor: line.credit > 0 ? '#dc2626' : '#e2e8f0',
                        background: line.credit > 0 ? '#fef2f2' : 'white'
                      }}
                    />
                  </div>
                </div>

                {/* Line Description */}
                <div className="mobile-input-group" style={{ marginTop: '12px', marginBottom: 0 }}>
                  <label className="mobile-label">Radkommentar (valfri)</label>
                  <input
                    type="text"
                    className="mobile-input"
                    placeholder="Valfri kommentar..."
                    value={line.description}
                    onChange={(e) => updateLine(idx, 'description', e.target.value)}
                    style={{ fontSize: '14px', padding: '10px 14px' }}
                  />
                </div>
              </div>
            ))}

            {/* Quick Templates */}
            <div className="mobile-card" style={{ background: '#f8fafc' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#64748b', marginBottom: '12px' }}>
                Snabbmallar
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {[
                  { label: 'Inköp kontanter', debit: '4000', credit: '1910' },
                  { label: 'Inköp kort', debit: '4000', credit: '1930' },
                  { label: 'Försäljning', debit: '1930', credit: '3001' },
                  { label: 'Hyra', debit: '5010', credit: '1930' }
                ].map((template, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      const debitAcc = accounts.find(a => a.account_number === template.debit);
                      const creditAcc = accounts.find(a => a.account_number === template.credit);
                      if (debitAcc && creditAcc) {
                        setEntry(prev => ({
                          ...prev,
                          lines: [
                            { account_number: debitAcc.account_number, account_name: debitAcc.name, debit: 0, credit: 0, description: '' },
                            { account_number: creditAcc.account_number, account_name: creditAcc.name, debit: 0, credit: 0, description: '' }
                          ]
                        }));
                      }
                    }}
                    style={{
                      padding: '8px 14px',
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '20px',
                      fontSize: '13px',
                      cursor: 'pointer'
                    }}
                  >
                    {template.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </main>

      {/* Save Button - Fixed at bottom */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #e2e8f0',
        padding: '16px',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
        zIndex: 100
      }}>
        {/* Summary */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '12px',
          padding: '12px',
          background: totals.isBalanced ? '#f0fdf4' : '#fef2f2',
          borderRadius: '10px'
        }}>
          <div>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Debet</div>
            <div style={{ fontWeight: 600, color: '#16a34a' }}>{totals.totalDebit.toLocaleString('sv-SE')} kr</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <ArrowRightLeft size={20} color={totals.isBalanced ? '#16a34a' : '#dc2626'} />
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '12px', color: '#64748b' }}>Kredit</div>
            <div style={{ fontWeight: 600, color: '#dc2626' }}>{totals.totalCredit.toLocaleString('sv-SE')} kr</div>
          </div>
        </div>

        <button 
          className="mobile-btn mobile-btn-primary"
          onClick={handleSave}
          disabled={saving || !totals.isBalanced}
          style={{ 
            opacity: totals.isBalanced ? 1 : 0.5,
            background: totals.isBalanced ? '#1e3a5f' : '#94a3b8'
          }}
        >
          {saving ? (
            <div className="mobile-spinner" style={{ width: '18px', height: '18px', borderTopColor: 'white' }} />
          ) : (
            <>
              <Calculator size={18} />
              {totals.isBalanced ? 'Spara verifikation' : 'Måste balansera först'}
            </>
          )}
        </button>
      </div>

      {/* Account Picker Sheet */}
      <div className={`mobile-overlay ${showAccountPicker !== null ? 'visible' : ''}`} onClick={() => setShowAccountPicker(null)} />
      <div className={`mobile-sheet ${showAccountPicker !== null ? 'open' : ''}`}>
        <div className="mobile-sheet-handle" />
        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Välj konto</h3>
        
        <div className="mobile-search" style={{ marginBottom: '16px' }}>
          <Search size={18} className="mobile-search-icon" />
          <input
            type="text"
            className="mobile-search-input"
            placeholder="Sök kontonummer eller namn..."
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
          />
        </div>

        {/* Common accounts */}
        {!accountSearch && commonAccounts.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>
              VANLIGA KONTON
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {commonAccounts.map(acc => (
                <button
                  key={acc.account_number}
                  onClick={() => selectAccount(showAccountPicker, acc)}
                  style={{
                    padding: '8px 12px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  {acc.account_number}
                </button>
              ))}
            </div>
          </div>
        )}
        
        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
          {filteredAccounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#64748b' }}>
              Inga konton hittades
            </div>
          ) : (
            filteredAccounts.slice(0, 50).map(account => (
              <div
                key={account.account_number}
                onClick={() => selectAccount(showAccountPicker, account)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderBottom: '1px solid #e2e8f0',
                  cursor: 'pointer'
                }}
              >
                <div style={{
                  minWidth: '60px',
                  fontWeight: 600,
                  color: '#1e3a5f'
                }}>
                  {account.account_number}
                </div>
                <div style={{ flex: 1, fontSize: '14px' }}>
                  {account.name}
                </div>
                {entry.lines[showAccountPicker]?.account_number === account.account_number && (
                  <Check size={18} color="#16a34a" />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
